export type StakesLevelKey = 'low' | 'mid' | 'high';

const STAKES_LABEL_PATTERN = /^(low|mid|medium|high)(\s+stakes?)?$/i;

function parseStakesLevelKey(value: string): StakesLevelKey | null {
  const normalized = value.toLowerCase().replace(/\s+/g, ' ').trim();

  if (normalized.includes('high')) {
    return 'high';
  }
  if (normalized.includes('mid') || normalized.includes('medium')) {
    return 'mid';
  }
  if (normalized.includes('low')) {
    return 'low';
  }

  return null;
}

export function isStakesLabel(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return false;
  }

  if (/stakes?/i.test(trimmed)) {
    return true;
  }

  return STAKES_LABEL_PATTERN.test(trimmed);
}

export function partitionGameTypes(gameTypes: string[]): {
  stakesLabels: string[];
  gameTypes: string[];
} {
  const stakesLabels: string[] = [];
  const filteredGameTypes: string[] = [];

  for (const label of gameTypes) {
    if (isStakesLabel(label)) {
      stakesLabels.push(label);
    } else {
      filteredGameTypes.push(label);
    }
  }

  return { stakesLabels, gameTypes: filteredGameTypes };
}

export function formatStakesLabel(label: string): string {
  const key = parseStakesLevelKey(label);
  if (key === 'low') {
    return 'Low Stakes';
  }
  if (key === 'mid') {
    return 'Mid Stakes';
  }
  if (key === 'high') {
    return 'High Stakes';
  }

  return label.trim();
}

export function formatStakesLine(stakesLabels: string[]): string {
  if (stakesLabels.length === 0) {
    return 'Not listed';
  }

  const formatted = stakesLabels.map(formatStakesLabel);
  return formatted.join(' • ');
}

export function getStakesLevelKeys(stakesLabels: string[]): StakesLevelKey[] {
  const keys = new Set<StakesLevelKey>();

  for (const label of stakesLabels) {
    const key = parseStakesLevelKey(label);
    if (key) {
      keys.add(key);
    }
  }

  return [...keys];
}

export function getPrimaryStakesLevelKey(stakesLabels: string[]): StakesLevelKey | null {
  if (stakesLabels.length === 0) {
    return null;
  }

  // Prefer the highest tier when multiple are listed.
  const keys = stakesLabels
    .map((label) => parseStakesLevelKey(label))
    .filter((key): key is StakesLevelKey => key !== null);

  if (keys.includes('high')) {
    return 'high';
  }
  if (keys.includes('mid')) {
    return 'mid';
  }
  if (keys.includes('low')) {
    return 'low';
  }

  return null;
}
